import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useWatch } from 'react-hook-form';
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Checkbox,
  Collapse,
  FormLabel,
  FormErrorMessage,
  FormControl,
  CheckboxGroup,
  Circle,
  Box,
  Text,
  Stack,
} from "@chakra-ui/react"
import ContactLinks from './ContactLinks';
import getContactSections from './getContactSections';

const SectionCheckboxGroup = ({ options, ...props }) => (
  <CheckboxGroup {...props} colorScheme="primary" size="sm">
    <Stack spacing="2">
      {options.map((opt, k) => (
        <Checkbox
          key={k}
          p="2"
          width="100%"
          bg="gray.50"
          value={opt}
          rounded="sm"
          color="gray.600"
        >{opt}</Checkbox>
      ))}
    </Stack>
  </CheckboxGroup>
)

const TheMatrix = ({
  sections,
  typeValue,
  control,
  defaultIndex,
  isEditing,
  isPreview,
}) => {
  const { okSection, indexMap } = useMemo(() => sections.reduce((res, section) => {
    if (section.steps.some(({ options }) => options.every(opt => !opt))) {
      res.indexMap.push(-1)
      return res
    }
    const newLength = res.okSection.push(section)
    res.indexMap.push(newLength - 1)
    return res
  }, { okSection: [], indexMap: [] }), [sections])
  const sectionHasSelection = indexMap.reduce((sel, id, orgId) => {
    if (id > -1) {
      const step1Selected = typeValue?.[orgId]?.[0]?.filter(Boolean) ?? []
      sel[id] = step1Selected.length > 0
    }
    return sel
  }, [])
  const [tabIndex, setTabIndex] = useState(defaultIndex)
  const ref = useRef()
  const handleTabsChange = (index) => {
    setTabIndex(index)
  }
  useEffect(() => {
    let ele = ref.current
    while (ele && !ele.classList?.contains('chakra-modal__content')) {
      ele = ele.parentNode
    }
    if (ele) {
      const handleIndexChange = (e) => {
        if (indexMap[e.detail.index] >= 0) {
          setTabIndex(indexMap[e.detail.index])
        }
      }
      ele.addEventListener('checkbox-config:onChange', handleIndexChange)
      return () => ele.removeEventListener('checkbox-config:onChange', handleIndexChange)
    }
  }, [indexMap])
  return (
    <Tabs
      px="2"
      colorScheme="primary"
      size="sm"
      isFitted
      variant="enclosed"
      index={tabIndex}
      defaultIndex={tabIndex}
      onChange={handleTabsChange}
      ref={ref}
    >
      {!isEditing && !isPreview && (
        <FormControl isRequired>
          <FormLabel mb="2">
            合作項目
          </FormLabel>
        </FormControl>
      )}
      <TabList>
        {okSection.map((sec, i) => (
          <Tab
            key={i}
            px="2"
            position="relative"
            _selected={{
              // fontWeight: 900,
              color: 'primary.500',
              borderColor: 'primary.500',
              borderBottomColor: 'white',
              // color: 'white',
            }}
          >
            <Box position="relative">
              {sec.title}
              {sectionHasSelection?.[i] > 0 && <Circle className="selected-indicator" position="absolute" top="0" right="-1.5" bg="primary.500" size="1" />}
            </Box>
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {okSection.map((sec, i) => (
          <TabPanel key={i} spacing="4" px="2" as={Stack}>
            {sec.steps.map((g, j) => (
              <Box key={j} as={j > 0 && control && Collapse} in={sectionHasSelection?.[i]} w="full">
                <FormControl>
                  <FormLabel fontSize="0.875rem" color="primary.500">
                    {sec.steps.length > 1 && `Step${j + 1}. `}{g.title} <Text as="span" color="gray.400">(可多選)</Text>
                  </FormLabel>
                  {control ? (
                    <Controller
                      name={`type.${sec.title}.${j}`}
                      control={control}
                      render={({ field }) => (
                        <SectionCheckboxGroup {...field} options={g.options} />
                      )}
                    />
                  ) : <SectionCheckboxGroup options={g.options} />}
                  <FormErrorMessage>至少要勾選一項喔</FormErrorMessage>
                </FormControl>
              </Box>
            ))}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  )
}

const LiveMatrix = ({ control, sections, defaultIndex }) => {
  const typeValue = useWatch({ control, name: sections.map(sec => `type.${sec.title}`) })

  return <TheMatrix typeValue={typeValue} control={control} sections={sections} defaultIndex={defaultIndex} />
}

const NeedsMatrix = ({
  control,
  template,
  templateConfig,
  action,
  description,
  defaultIndex,
  isPreview,
  isEditing,
}) => {
  const liveKey = JSON.stringify(templateConfig)
  const sections = useMemo(() => getContactSections({ template, templateConfig }), [template, liveKey])
  if (!sections.length) return <ContactLinks action={action} description={description} isEditing={isEditing} isPreview={isPreview} />
  return control ? <LiveMatrix control={control} sections={sections} defaultIndex={defaultIndex} /> : <TheMatrix sections={sections} defaultIndex={defaultIndex} isEditing={isEditing} isPreview={isPreview} />
}

export default NeedsMatrix
